import { useEffect, useRef } from 'react';
import * as Sentry from '@sentry/react';
import { getRunBySession } from '@/services/workflow/get-run-by-session';
import type { WorkflowRunWithResults } from '@/services/workflow/get-workflow-run';
import {
  reconcileRunState,
  mergeNodeStatuses,
} from '@/pages/workflow/helper/reconcile-run-state';
import { useWorkflowStore } from '@/stores/workflow-store';
import { useNodeStore } from '@/stores/node-store';
import { websocketService } from '@/services/websocket/websocket-service';
import type {
  NodeStatusEvent,
  PauseStateSnapshot,
} from '@/types/workflow-types';

const MAX_RECONCILE_ATTEMPTS = 3;
const RECONCILE_RETRY_MS = 2000;

export interface ApplyReconcileFromRunParams {
  run: WorkflowRunWithResults;
  isPausingRef: React.RefObject<boolean>;
  isResumingRef: React.RefObject<boolean>;
  handleExecutionResultEvent: (event: NodeStatusEvent) => void;
  handleTerminationEvent: (event: NodeStatusEvent) => void;
  setNodeStatuses: React.Dispatch<
    React.SetStateAction<Record<string, NodeStatusEvent[]>>
  >;
  setIsPaused: (value: boolean) => void;
  setPauseState: (value: PauseStateSnapshot | null) => void;
  /**
   * When true, disconnect the workflow WebSocket after applying a terminal
   * reconcile (post-reconnect path — run is done, no more events to stream).
   */
  disconnectWebSocketOnTerminal?: boolean;
}

/**
 * Applies a backend run snapshot to the canvas: hydrates node results,
 * merges synthetic node events, syncs pause state, and routes terminal
 * events through the same handlers as live WebSocket messages.
 */
export function applyReconcileFromRun({
  run,
  isPausingRef,
  isResumingRef,
  handleExecutionResultEvent,
  handleTerminationEvent,
  setNodeStatuses,
  setIsPaused,
  setPauseState,
  disconnectWebSocketOnTerminal = false,
}: ApplyReconcileFromRunParams): void {
  if (useWorkflowStore.getState().currentSession !== run.session_id) {
    return;
  }

  const outcome = reconcileRunState(run);

  const currentNodes = useNodeStore.getState().nodes;
  const { updateNodeResult } = useNodeStore.getState();
  for (const { nodeId, data } of outcome.nodeResultPayloads) {
    const existingNode = currentNodes.find((n) => n.id === nodeId);
    if (existingNode?.data?.result) continue;
    updateNodeResult(nodeId, data);
  }

  if (Object.keys(outcome.nodeEvents).length > 0) {
    setNodeStatuses((prev) => mergeNodeStatuses(prev, outcome.nodeEvents));
  }

  if (!isPausingRef.current && !isResumingRef.current) {
    setIsPaused(outcome.isPaused);
    setPauseState(outcome.isPaused ? (run.pause_state ?? null) : null);
  }

  if (outcome.terminalEvent) {
    if (outcome.terminalEvent.kind === 'execution_result') {
      handleExecutionResultEvent(outcome.terminalEvent.event);
    } else {
      handleTerminationEvent(outcome.terminalEvent.event);
    }

    if (disconnectWebSocketOnTerminal) {
      websocketService.disconnect(
        'Run already terminated (reconciled after reconnect)',
      );
    }
  }
}

export interface UseReconcileOnReconnectOptions {
  isConnected: boolean;
  workflowId: string | undefined;
  sessionId: string | null;
  orgId: string | undefined;
  isExecutingWorkflowRef: React.RefObject<boolean>;
  isPausingRef: React.RefObject<boolean>;
  isResumingRef: React.RefObject<boolean>;
  handleExecutionResultEvent: (event: NodeStatusEvent) => void;
  handleTerminationEvent: (event: NodeStatusEvent) => void;
  setNodeStatuses: React.Dispatch<
    React.SetStateAction<Record<string, NodeStatusEvent[]>>
  >;
  setIsPaused: (value: boolean) => void;
  setPauseState: (value: PauseStateSnapshot | null) => void;
}

/** Reconcile deps for {@link useWorkflowStatusToasts} (WS ids come from stores). */
export type WorkflowStatusToastsReconcileOptions = Omit<
  UseReconcileOnReconnectOptions,
  'isConnected' | 'workflowId' | 'sessionId' | 'orgId'
>;

// The WS protocol is delta-only, so a run that finishes while the laptop is
// closed emits no terminal event on reconnect and the status bar would stay on
// "Running" forever. Fetches the authoritative snapshot instead. Retries 3x,
// 2s apart, and only on REconnects within a session.
export function useReconcileOnReconnect({
  isConnected,
  workflowId,
  sessionId,
  orgId,
  isExecutingWorkflowRef,
  isPausingRef,
  isResumingRef,
  handleExecutionResultEvent,
  handleTerminationEvent,
  setNodeStatuses,
  setIsPaused,
  setPauseState,
}: UseReconcileOnReconnectOptions): void {
  const prevWsConnectedRef = useRef(isConnected);
  const reconcileInFlightRef = useRef(false);
  const reconcileRetryRef = useRef<NodeJS.Timeout | null>(null);
  const reconcileAttemptRef = useRef(0);

  const handleExecutionResultEventRef = useRef(handleExecutionResultEvent);
  handleExecutionResultEventRef.current = handleExecutionResultEvent;
  const handleTerminationEventRef = useRef(handleTerminationEvent);
  handleTerminationEventRef.current = handleTerminationEvent;

  // Stores the session_id that was active when a disconnect was
  // observed.  Prevents the reconcile from firing on the INITIAL
  // connect of a fresh run — only fires on a REconnect within the
  // SAME session that experienced the disconnect.
  const disconnectedSessionRef = useRef<string | null>(null);

  useEffect(() => {
    const wasConnected = prevWsConnectedRef.current;
    prevWsConnectedRef.current = isConnected;

    // Arm: connected → disconnected while a run is active.
    if (wasConnected && !isConnected && isExecutingWorkflowRef.current) {
      disconnectedSessionRef.current = sessionId;
      return;
    }

    // Only act on false → true transitions that follow a prior
    // disconnect within the SAME session.
    if (wasConnected || !isConnected) return;
    if (!isExecutingWorkflowRef.current) return;
    if (disconnectedSessionRef.current !== sessionId) return;
    if (reconcileInFlightRef.current) return;

    reconcileAttemptRef.current = 0;

    if (!workflowId || !sessionId || !orgId) return;

    const doReconcile = async () => {
      reconcileInFlightRef.current = true;
      reconcileAttemptRef.current += 1;
      try {
        const response = await getRunBySession(workflowId, sessionId, orgId);
        if (response.detail || !response.data) {
          throw new Error(response.detail || 'Empty response');
        }

        if (useWorkflowStore.getState().currentSession !== sessionId) {
          disconnectedSessionRef.current = null;
          return;
        }

        applyReconcileFromRun({
          run: response.data,
          isPausingRef,
          isResumingRef,
          handleExecutionResultEvent: handleExecutionResultEventRef.current,
          handleTerminationEvent: handleTerminationEventRef.current,
          setNodeStatuses,
          setIsPaused,
          setPauseState,
          disconnectWebSocketOnTerminal: true,
        });

        disconnectedSessionRef.current = null;
      } catch (error) {
        console.error('[useReconcileOnReconnect] Reconcile failed', error);
        Sentry.captureException(error, {
          extra: {
            context: 'post-reconnect-reconcile',
            workflowId,
            sessionId,
            attempt: reconcileAttemptRef.current,
          },
        });

        if (
          reconcileAttemptRef.current < MAX_RECONCILE_ATTEMPTS &&
          isExecutingWorkflowRef.current &&
          disconnectedSessionRef.current === sessionId
        ) {
          reconcileRetryRef.current = setTimeout(() => {
            reconcileRetryRef.current = null;
            if (
              isExecutingWorkflowRef.current &&
              disconnectedSessionRef.current === sessionId
            ) {
              void doReconcile();
            }
          }, RECONCILE_RETRY_MS);
        } else {
          disconnectedSessionRef.current = null;
        }
      } finally {
        reconcileInFlightRef.current = false;
      }
    };

    void doReconcile();

    return () => {
      if (reconcileRetryRef.current) {
        clearTimeout(reconcileRetryRef.current);
        reconcileRetryRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected, workflowId, sessionId, orgId]);
}
